<template>
  <el-form ref="form" :model="personality" label-position="top">
    <h1 class="mb0">About Personality</h1>

    <p class="quote mt1">
      “Life stained glass, our different personalities <br>
      Reflect God's light in many colors and patterns.” <br>
      — Rick Warren
    </p>

    <p>
      Understanding the personality God has given you will help you more effectively express your
      spiritual gifts, heart, and abilities for HIS sake.
    </p>

    <p>
      We are going to focus on only two aspects of your personality: <br>
      How you are <b>Energized</b> and how you are <b>Organized</b>.
    </p>

    <ul>
      <li>
        <p>
          For each statement, click the circle closer to the statement that best describes what you would
          prefer in most situations.
        </p>
      </li>
      <li>
        <p>
          Do not answer according to what you feel is expected by a spouse, family member, employer, etc.
        </p>
      </li>
      <li>
        <p>
          Select the behavior or perspective that would come naturally to you if you knew there were NO restrictions
          on or consequences for your personal expression.
        </p>
      </li>
    </ul>

    <h2>How are you Energized?</h2>

    <el-form-item
        class="personality-question"
        v-for="question in energizedQuestions"
        :key="question.question"
        :label="question.question"
        :prop="question.key"
        :rules="[{ required: true, message: 'Required' }]">
      <el-row :gutter="20">
        <el-col :xs="24" :sm="8">
          <div class="left-side">
            {{ question.left }}
          </div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="center-side">
            <el-radio-group :value="personality[question.key]" @input="storePersonalityValue(question.key, arguments[0])">
              <el-radio :label="1">1</el-radio>
              <el-radio :label="2">2</el-radio>
              <el-radio :label="3">3</el-radio>
              <el-radio :label="4">4</el-radio>
            </el-radio-group>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="right-side">
            {{ question.right }}
          </div>
        </el-col>
      </el-row>
    </el-form-item>

    <h2>How are you Organized?</h2>

    <el-form-item
        class="personality-question"
        v-for="question in organizedQuestions"
        :key="question.question"
        :label="question.question"
        :prop="question.key"
        :rules="[{ required: true, message: 'Required' }]">
      <el-row :gutter="20">
        <el-col :xs="24" :sm="8">
          <div class="left-side">
            {{ question.left }}
          </div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="center-side">
            <el-radio-group :value="personality[question.key]" @input="storePersonalityValue(question.key, arguments[0])">
              <el-radio :label="1">1</el-radio>
              <el-radio :label="2">2</el-radio>
              <el-radio :label="3">3</el-radio>
              <el-radio :label="4">4</el-radio>
            </el-radio-group>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="right-side">
            {{ question.right }}
          </div>
        </el-col>
      </el-row>
    </el-form-item>

    <p>&nbsp;</p>

    <ui-form-actions>
      <el-button @click="goBack">Back</el-button>
      <el-button type="primary" @click="goForward">Continue</el-button>
    </ui-form-actions>
  </el-form>
</template>

<script>
  /* eslint-disable max-len */

  import step from '../mixins/step';
  import validation from '../mixins/validation';

  export default {
    mixins: [step, validation],
    name: 'Personality',
    methods: {
      goBack() {
        this.$router.back();
      },
      goForward() {
        this.validate().then(() => {
          this.$router.push({ path: '/experience' });
        }).catch(() => {
        });
      },
      storePersonalityValue(key, value) {
        this.$store.commit('setPersonalityByKey', { key, value });
      },
    },
    computed: {
      personality() {
        return this.$store.state.personality;
      },
    },
    data() {
      return {
        energizedQuestions: [
          {
            question: 'I\'m more comfortable:',
            key: 'comfortable',
            left: 'Doing things for people',
            right: 'Being with people',
          },
          {
            question: 'When doing a task, I tend to:',
            key: 'task',
            left: 'Focus on the goal',
            right: 'Focus on relationships',
          },
          {
            question: 'I get more excited about:',
            key: 'excited',
            left: 'Advancing a cause',
            right: 'Creating community',
          },
          {
            question: 'I feel I\'ve accomplished something when I\'ve:',
            key: 'accomplished',
            left: 'Gotten a job done',
            right: 'Built a relationship',
          },
          {
            question: 'It is important to start a meeting:',
            key: 'meeting',
            left: 'On time',
            right: 'When everyone gets there',
          },
          {
            question: 'I\'m more concerned with:',
            key: 'concerned',
            left: 'Meeting a deadline',
            right: 'Maintaining the team',
          },
          {
            question: 'I place higher value on:',
            key: 'value',
            left: 'Action',
            right: 'Communication',
          },
        ],
        organizedQuestions: [
          {
            question: 'While on vacation I prefer to:',
            key: 'vacation',
            left: 'Be spontaneous',
            right: 'Follow a set plan',
          },
          {
            question: 'I prefer to set guidelines that are:',
            key: 'guidelines',
            left: 'General',
            right: 'Specific',
          },
          {
            question: 'I prefer projects that have:',
            key: 'projects',
            left: 'Variety',
            right: 'Routine',
          },
          {
            question: 'I like to:',
            key: 'like',
            left: 'Play it by ear',
            right: 'Stick to a plan',
          },
          {
            question: 'I find routine:',
            key: 'routine',
            left: 'Boring',
            right: 'Restful',
          },
          {
            question: 'I accomplish tasks best:',
            key: 'tasks',
            left: 'By working it out as I go',
            right: 'By following a plan',
          },
        ],
      };
    },
  };
</script>

<style lang="scss">
  @import "~styles/vars";
  @import "~element-theme/mixins/mixins.scss";

  .slider-example {
    border: 1px solid $--border-color-base;
    border-radius: 4px;
  }

  .personality-question {
    border-bottom: 1px dotted $--border-color-base;
    margin: 0;
    padding: 1rem 0;

    .el-form-item__label {
      font-weight: 500;
    }

    .el-form-item__error {
      top: 0;
    }

    .left-side, .right-side {
      font-size: 12px;
      @include res(sm) {
        font-size: $--font-size-base;
        font-weight: 500;
      }
    }

    .left-side {
      text-align: center;
      @include res(sm) {
        margin: .5rem 0 0;
        text-align: right;
      }
    }

    .right-side {
      text-align: center;
      @include res(sm) {
        margin: .5rem 0 0;
        text-align: left;
      }
    }

    .center-side {
      margin: .4rem 0;
      text-align: center;
      @include res(sm) {
        margin: 0;
      }
    }

    .el-radio-group {
      margin: 0 auto;

      @include res(sm) {
        display: flex;
        justify-content: space-between;
        margin: 0 auto;
        max-width: 200px;
        .el-radio__label {
          display: none;
        }
      }

      .el-radio {
        display: block;
        margin: .25rem 0;
      }

      .el-radio + .el-radio {
        margin-left: auto;
      }

      .el-radio__inner {
        height: 28px;
        width: 28px;
        &::after {
          height: 8px;
          width: 8px;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/Personality.vue